/**
 * AuthProvider — wraps the app, persists JWT in localStorage, manages cookie
 * sessions, and prefetches the CSRF token after login for cookie-session
 * mutations. The context, hooks, and persisted-state helpers live in auth.ts
 * (this file only exports the component — react-refresh/only-export-components).
 */

import { useState, useCallback, useEffect, type ReactNode } from 'react';
import {
  AuthContext,
  AUTH_STORAGE_KEY,
  loadPersistedAuth,
  type AuthOrg,
  type AuthState,
  type AuthUser,
} from './auth';
import {
  fetchCsrfToken,
  clearCsrfToken,
  getCsrfToken,
  readCsrfCookie,
  isCookieSessionActive,
  hasCsrfCookie,
} from './csrf';

const API_BASE = import.meta.env.VITE_PUBLIC_API_V2_URL || 'http://localhost:3002';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(loadPersistedAuth);
  // Flipped by the /auth/me revalidation effect below, which always runs on
  // mount (the session cookie is httpOnly, so there is no client-side signal
  // that could rule out a live session without asking the server).
  const [authResolved, setAuthResolved] = useState<boolean>(false);

  const login = useCallback((token: string | null, user: AuthUser, org: AuthOrg) => {
    const newState: AuthState = { token, user, org, isAuthenticated: true };
    setState(newState);
    setAuthResolved(true);
    // Prefetch CSRF token to detect cookie session availability.
    // Cookie session active: purge any stale JWT-era localStorage entry so a
    // later reload can't restore an org without entitlements.
    fetchCsrfToken()
      .then(() => {
        if (isCookieSessionActive()) {
          localStorage.removeItem(AUTH_STORAGE_KEY);
        } else {
          localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ token, user, org }));
        }
      })
      .catch(() => {
        // Cookies not working — persist minimal state to localStorage as fallback
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ token, user, org }));
      });
  }, []);

  const logout = useCallback(() => {
    setState({ token: null, user: null, org: null, isAuthenticated: false });
    setAuthResolved(true);
    localStorage.removeItem(AUTH_STORAGE_KEY);
    // POST /auth/logout sits behind the global CSRF hook — capture the token
    // BEFORE clearing it locally, or server-side revocation silently 403s.
    const csrf = getCsrfToken() ?? readCsrfCookie();
    clearCsrfToken();
    // Clear server-side cookie session (fire-and-forget). Raw fetch, not
    // apiV2Fetch: the wrapper dispatches auth:logout on 401, re-entering here.
    fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      ...(csrf ? { headers: { 'X-CSRF-Token': csrf } } : {}),
    }).catch(() => { /* non-fatal — cookie will expire naturally */ });
  }, []);

  const updateUser = useCallback((partial: Partial<AuthUser>) => {
    setState((prev) => {
      if (!prev.user) return prev;
      const updated = { ...prev, user: { ...prev.user, ...partial } };
      // Only persist to localStorage when cookie sessions are not active
      if (!isCookieSessionActive() && updated.token && updated.user && updated.org) {
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({
          token: updated.token, user: updated.user, org: updated.org,
        }));
      }
      return updated;
    });
  }, []);

  // Prefetch CSRF token on mount if already authenticated (page reload)
  useEffect(() => {
    if (state.isAuthenticated) {
      fetchCsrfToken().catch(() => { /* non-fatal */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  // Server-truth revalidation: call /auth/me and overwrite user/org with the
  // server response. Runs on mount (below) — even when localStorage restored
  // an authenticated state, which is only a paint-fast hint and may be a stale
  // JWT-era entry whose org lacks entitlements — and on demand after flows
  // whose response org omits entitlements (accept-invite).
  //
  // The CSRF cookie (24h) expires long before the session cookie (30d), so a
  // missing CSRF cookie is NOT proof there is no session — and the session
  // cookie is httpOnly, invisible to JS. Fetch a fresh CSRF token first
  // (public endpoint) and probe /auth/me unconditionally; an idle-but-valid
  // session recovers instead of being silently logged out.
  const revalidateAuth = useCallback(async (): Promise<void> => {
    if (!hasCsrfCookie()) {
      await fetchCsrfToken();
    }
    // Raw fetch, not apiV2Fetch: a 401 here is an expected probe outcome
    // (no session), and the wrapper would dispatch a global auth:logout.
    const res = await fetch(`${API_BASE}/auth/me`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) return;
    const data = await res.json();
    if (!data?.user || !data?.org) return;
    setState({
      token: null, // No JWT needed — session cookie handles auth
      user: data.user,
      org: data.org,
      isAuthenticated: true,
    });
    // Cookie session is authoritative — drop any stale JWT-era entry
    localStorage.removeItem(AUTH_STORAGE_KEY);
    // Ensure CSRF token is ready for mutations (skip if the pre-probe
    // fetch above already populated it)
    if (!getCsrfToken()) {
      fetchCsrfToken().catch(() => { /* non-fatal */ });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    revalidateAuth()
      .catch(() => {
        // Cookie session invalid or expired — keep whatever local state we have;
        // a real 401 on the next API call triggers auth:logout below.
      })
      .finally(() => {
        if (!cancelled) setAuthResolved(true);
      });

    return () => { cancelled = true; };
  }, [revalidateAuth]); // Stable callback — still runs only on mount

  // Listen for 401 events from API to auto-logout
  useEffect(() => {
    const handler = () => logout();
    window.addEventListener('auth:logout', handler);
    return () => window.removeEventListener('auth:logout', handler);
  }, [logout]);

  return (
    <AuthContext.Provider value={{ ...state, authResolved, login, logout, updateUser, revalidateAuth }}>
      {children}
    </AuthContext.Provider>
  );
}
